import React, { useContext, useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Checkbox } from "primereact/checkbox";
import Header from "../components/Header";
import CardStandard from "../components/CardStandard";
import "react-range-slider-input/dist/style.css";
import { useLocation } from "react-router-dom";
import { UnitFieldSymbols } from "../components/input/fields/input-with-symbols";
import LoadingBar from '../components/loadingBar/LoadingBar'; 
import { Calendar } from "primereact/calendar";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataContext } from "../App";
import { GetAllAvailableProperties } from "../utils/functions";
import { ChevronLeft, ChevronRight } from "react-feather";
import Seo from "../components/Seo";
import Accordion from "react-bootstrap/Accordion";

const PropertyCollection = () => {
  const { t } = useTranslation();
  const context = useContext(DataContext);
  const location = useLocation();
  const [isMobileTablet, setIsMobileTablet] = useState(
    window.matchMedia("(max-width: 991px)").matches
  );

  useEffect(() => {
    const handleResize = () =>
      setIsMobileTablet(window.matchMedia("(max-width: 991px)").matches);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const parseDate = (dateStringOrObject) => {
    if (!dateStringOrObject) return null;
    if (dateStringOrObject instanceof Date && !isNaN(dateStringOrObject)) return dateStringOrObject;
    const date = new Date(dateStringOrObject);
    return isNaN(date.getTime()) ? null : date;
  };

  const initialLocationState = location.state || {};
  const {
    propertyType: initialPropertyType,
    searchedCheckIn: initialSearchedCheckIn,
    searchedCheckOut: initialSearchedCheckOut,
    searchedGuests: initialSearchedGuests,
    searchedProperty: initialSearchedProperty,
    searchedDestination: initialSearchedDestination,
    searchedBathrooms: initialSearchedBathrooms,
    searchedBedrooms: initialSearchedBedrooms,
    searchedCheckboxes: initialSearchedCheckboxes,
  } = initialLocationState;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const propertiesSource = useMemo(() => context?.properties
    ? context.properties.filter((x) => x.status === "active")
    : [], [context?.properties]);

  const [properties, setProperties] = useState(propertiesSource);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  const defaultSortBy = useMemo(() => ({
    value: "number-rooms-ascending",
    label: t("NumberRoomsAscending"),
  }), [t]);

  const defaultCheckboxes = useMemo(() => ({
    privatePool: false, heatedPool: false, airCon: false, hotTub: false,
    jacuzzi: false, weddingVenue: false, wifi: false, seaView: false,
    walkToBeach: false, walkToVillage: false,
  }), []);

  const [isVilla, setIsVilla] = useState(initialPropertyType === "villa");
  const [isApartment, setIsApartment] = useState(initialPropertyType === "apartment");
  const [checkInDate, setCheckInDate] = useState(parseDate(initialSearchedCheckIn));
  const [checkOutDate, setCheckOutDate] = useState(parseDate(initialSearchedCheckOut));
  const [sortBy, setSortBy] = useState(defaultSortBy);


  const [stagedProperty, setStagedProperty] = useState(initialSearchedProperty || null);
  const [stagedDestination, setStagedDestination] = useState(initialSearchedDestination || null);
  const [stagedBedrooms, setStagedBedrooms] = useState(initialSearchedBedrooms || 0);
  const [stagedBathrooms, setStagedBathrooms] = useState(initialSearchedBathrooms || 0);
  const [stagedGuests, setStagedGuests] = useState(initialSearchedGuests || null);
  const [stagedCheckboxes, setStagedCheckboxes] = useState({
    ...defaultCheckboxes,
    ...(initialSearchedCheckboxes || {}),
  });

  const [activeAppliedProperty, setActiveAppliedProperty] = useState(initialSearchedProperty || null);
  const [activeAppliedDestination, setActiveAppliedDestination] = useState(initialSearchedDestination || null);
  const [activeAppliedBedrooms, setActiveAppliedBedrooms] = useState(initialSearchedBedrooms || 0);
  const [activeAppliedBathrooms, setActiveAppliedBathrooms] = useState(initialSearchedBathrooms || 0);
  const [activeAppliedGuests, setActiveAppliedGuests] = useState(initialSearchedGuests || null);
  const [activeAppliedCheckboxes, setActiveAppliedCheckboxes] = useState({
    ...defaultCheckboxes,
    ...(initialSearchedCheckboxes || {}),
  });


  const [totalDisplayed, setTotalDisplayed] = useState(0);
  const [isEnvironmentOpen, setIsEnvironmentOpen] = useState(false);
  const [isVillaOpen, setIsVillaOpen] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      const basePropsForAvailability = propertiesSource.filter(prop =>
        (activeAppliedBedrooms ? prop.bedrooms >= activeAppliedBedrooms : true) &&
        (activeAppliedBathrooms ? prop.bathrooms >= activeAppliedBathrooms : true) &&
        (activeAppliedGuests ? prop.guests >= activeAppliedGuests : true) &&
        (activeAppliedCheckboxes.privatePool ? prop.amenities.includes("PrivatePool") : true) &&
        (activeAppliedCheckboxes.heatedPool ? prop.amenities.includes("HeatedPool") : true) &&
        (activeAppliedCheckboxes.airCon ? prop.amenities.includes("AirCon") : true) &&
        (activeAppliedCheckboxes.hotTub ? prop.amenities.includes("HotTub") : true) &&
        (activeAppliedCheckboxes.jacuzzi ? prop.amenities.includes("Jacuzzi") : true) &&
        (activeAppliedCheckboxes.weddingVenue ? prop.amenities.includes("WeddingVenue") : true) &&
        (activeAppliedCheckboxes.wifi ? prop.amenities.includes("Wifi") : true) &&
        (activeAppliedCheckboxes.seaView ? prop.amenities.includes("SeaView") : true) &&
        (activeAppliedCheckboxes.walkToBeach ? prop.amenities.includes("WalkToBeach") : true) &&
        (activeAppliedCheckboxes.walkToVillage ? prop.amenities.includes("WalkToVillage") : true) &&
        (activeAppliedDestination ? activeAppliedDestination === prop.address?.area : true) &&
        (activeAppliedProperty ? activeAppliedProperty === prop.name : true) &&
        (isVilla && !isApartment ? prop.category === "villa" : !isVilla && isApartment ? prop.category === "apartment" : true)
      );

      const idsForAvailabilityCheck = basePropsForAvailability
        .filter(p => p.idGuesty !== null && p.idGuesty !== "")
        .map(p => p.idGuesty);

      if (checkInDate && checkOutDate && idsForAvailabilityCheck.length > 0) {
        setIsLoadingAvailability(true);
        try {
          const response = await GetAllAvailableProperties(
            idsForAvailabilityCheck,
            checkInDate,
            checkOutDate
          );
          setAvailabilityData(Array.isArray(response) ? response : []);
        } catch (error) {
          console.error("Failed to fetch availability:", error);
          setAvailabilityData([]);
        } finally {
          setIsLoadingAvailability(false);
        }
      } else {
        setAvailabilityData(null);
      }
    };

    if (checkInDate && checkOutDate) {
        fetchAvailability();
    } else {
        setAvailabilityData(null);
    }
  }, [
    checkInDate, checkOutDate, isVilla, isApartment, 
    propertiesSource, activeAppliedProperty, activeAppliedDestination, activeAppliedBedrooms, activeAppliedBathrooms, activeAppliedGuests, activeAppliedCheckboxes
  ]);

  useEffect(() => {
    let filteredResult = [...propertiesSource];

    filteredResult = filteredResult.filter(prop =>
        (isVilla && !isApartment ? prop.category === "villa" : !isVilla && isApartment ? prop.category === "apartment" : true)
    );

    filteredResult = filteredResult.filter(prop =>
      (activeAppliedBedrooms ? prop.bedrooms >= activeAppliedBedrooms : true) &&
      (activeAppliedBathrooms ? prop.bathrooms >= activeAppliedBathrooms : true) &&
      (activeAppliedGuests ? prop.guests >= activeAppliedGuests : true) &&
      (activeAppliedCheckboxes.privatePool ? prop.amenities.includes("PrivatePool") : true) &&
      (activeAppliedCheckboxes.heatedPool ? prop.amenities.includes("HeatedPool") : true) &&
      (activeAppliedCheckboxes.airCon ? prop.amenities.includes("AirCon") : true) &&
      (activeAppliedCheckboxes.hotTub ? prop.amenities.includes("HotTub") : true) &&
      (activeAppliedCheckboxes.jacuzzi ? prop.amenities.includes("Jacuzzi") : true) &&
      (activeAppliedCheckboxes.weddingVenue ? prop.amenities.includes("WeddingVenue") : true) &&
      (activeAppliedCheckboxes.wifi ? prop.amenities.includes("Wifi") : true) &&
      (activeAppliedCheckboxes.seaView ? prop.amenities.includes("SeaView") : true) &&
      (activeAppliedCheckboxes.walkToBeach ? prop.amenities.includes("WalkToBeach") : true) &&
      (activeAppliedCheckboxes.walkToVillage ? prop.amenities.includes("WalkToVillage") : true) &&
      (activeAppliedDestination ? activeAppliedDestination === prop.address?.area : true) &&
      (activeAppliedProperty ? activeAppliedProperty === prop.name : true)
    );

    if (checkInDate && checkOutDate && availabilityData) {
      filteredResult = filteredResult
        .filter(p => {
          const idGuesty = p.idGuesty;
          if (!idGuesty) return true;
          return availabilityData.some(avail => avail.idGuesty === idGuesty);
        })
        .map(p => {
          if (!p.idGuesty) return p;
          const priceInfo = availabilityData.find(avail => avail.idGuesty === p.idGuesty);
          return { ...p, price: priceInfo ? priceInfo.price ?? p.price ?? 0 : p.price ?? 0 };
        });
    }

    if (sortBy && sortBy.value) {
        const getTypologyScore = (typology) => (typology === "basic" ? 1 : typology === "premium" ? 2 : typology === "luxury" ? 3 : 0);
        if (sortBy.value === "number-rooms-ascending") {
            filteredResult.sort((a, b) => (a.bathrooms + a.bedrooms) - (b.bathrooms + b.bedrooms));
        } else if (sortBy.value === "number-rooms-decreasing") {
            filteredResult.sort((a, b) => (b.bathrooms + b.bedrooms) - (a.bathrooms + b.bedrooms));
        } else if (sortBy.value === "classification-ascending") {
            filteredResult.sort((a, b) => getTypologyScore(a.typology) - getTypologyScore(b.typology));
        } else if (sortBy.value === "classification-descending") {
            filteredResult.sort((a, b) => getTypologyScore(b.typology) - getTypologyScore(a.typology));
        }
    }
    setProperties(filteredResult);
    setCurrentPage(1); 

  }, [
    propertiesSource,
    isVilla, isApartment, checkInDate, checkOutDate, sortBy,
    activeAppliedProperty, activeAppliedDestination, activeAppliedBedrooms, activeAppliedBathrooms, activeAppliedGuests, activeAppliedCheckboxes,
    availabilityData 
  ]);

  const handleApplyFilters = () => {
    setActiveAppliedProperty(stagedProperty);
    setActiveAppliedDestination(stagedDestination);
    setActiveAppliedBedrooms(stagedBedrooms);
    setActiveAppliedBathrooms(stagedBathrooms);
    setActiveAppliedGuests(stagedGuests);
    setActiveAppliedCheckboxes(stagedCheckboxes);
  };

  const addDays = (date, days) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  };

  const toggleEnvironmentList = () => setIsEnvironmentOpen(!isEnvironmentOpen);
  const toggleVillaList = () => setIsVillaOpen(!isVillaOpen);

  const nextPage = () => {
    if (currentPage < Math.ceil(properties.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleGlobalResetFilters = () => {
    setIsVilla(false);
    setIsApartment(false);
    setCheckInDate(null);
    setCheckOutDate(null);
    setSortBy(defaultSortBy);

    setStagedProperty(null);
    setStagedDestination(null);
    setStagedBedrooms(0);
    setStagedBathrooms(0);
    setStagedGuests(null);
    setStagedCheckboxes(defaultCheckboxes);

    setActiveAppliedProperty(null);
    setActiveAppliedDestination(null);
    setActiveAppliedBedrooms(0);
    setActiveAppliedBathrooms(0);
    setActiveAppliedGuests(null);
    setActiveAppliedCheckboxes(defaultCheckboxes);
  };


  const handleStagedBedroomChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setStagedBedrooms(isNaN(value) ? 0 : value);
  };

  const handleStagedBathroomChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setStagedBathrooms(isNaN(value) ? 0 : value);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = properties.slice(indexOfFirstItem, indexOfLastItem);

   useEffect(() => {
    setTotalDisplayed(properties.length > 0 ? Math.min(indexOfLastItem, properties.length) : 0);
  }, [currentPage, properties, indexOfLastItem]);

  const sortingOptions = useMemo(() => [
    { value: "number-rooms-ascending", label: t("NumberRoomsAscending") },
    { value: "number-rooms-decreasing", label: t("NumberRoomsDecreasing") },
    { value: "classification-ascending", label: t("PropertyClassificationAscending") },
    { value: "classification-descending", label: t("PropertyClassificationDescending") },
  ], [t]);

  const areaOptions = useMemo(() => propertiesSource.reduce((acc, prop) => {
    if (prop.address?.area && !acc.some((option) => option.value === prop.address.area)) {
      acc.push({ value: prop.address.area, label: prop.address.area });
    }
    return acc;
  }, []), [propertiesSource]);

  const propertyOptions = useMemo(() => propertiesSource.reduce((acc, prop) => {
    if (prop.name && !acc.some((option) => option.value === prop.name)) {
      acc.push({ value: prop.name, label: prop.name });
    }
    return acc;
  }, []), [propertiesSource]);

  const guestOptions = useMemo(() => [
    ...Array.from(
      {
        length: propertiesSource.reduce(
          (max, prop) => Math.max(max, prop.guests || 1),
          1
        ),
      },
      (_, i) => ({ value: i + 1, label: (i + 1).toString() })
    ),
  ], [propertiesSource]);

  const today = useMemo(() => new Date(), []);
  const minGlobalDate = useMemo(() => addDays(today, 1), [today]);

  const checkboxLabels = {
    privatePool: t('PrivatePool'),
    heatedPool: t('HeatedPool'),
    airCon: t('AirCon'),
    hotTub: t('HotTub'),
    jacuzzi: t('Jacuzzi'),
    weddingVenue: t('WeddingVenue'),
    wifi: t('Wifi'),
    seaView: t('SeaView'),
    walkToBeach: t('WalkToBeach'),
    walkToVillage: t('WalkToVillage'),
  };


  return (
    <>
      <Seo
        title={"Property collection | Feeling Italy"}
        description={
          "Explore Feeling Italy's curated collection of luxury properties across the Amalfi Coast, Sorrento, and beyond. Find your perfect Mediterranean villa for an unforgettable holiday experience."
        }
        keywords={
          "Luxury Villas, Amalfi Coast Rentals, Mediterranean Vacation Homes, Sorrento Holiday Villas, Exclusive Italian Properties, Feeling Italy Villas, Villa Collection, Premium Vacation Rentals, Amalfi Coast Accommodation, Italian Villa Holidays"
        }
        url={window.location.href}
      />
      <Header background={"black"} />
      <div
        className="container-property-collection overflow-hidden"
        id="property-collection"
      >
        <div className="row">
          {!isMobileTablet ? (
            <div className="col-lg-3 mt-5 padding-filters d-md-none d-lg-block pb-5">
              <div className="border-custom no-radius">
                <div className="header-card-property pt-3 pb-2 mx-4 d-flex justify-content-between border-bottom">
                  <span className="mb-3 paragraph">{t("Filter")}</span>
                  <span
                    className={"cursor paragraph"}
                    onClick={handleGlobalResetFilters} 
                  >
                    {t("ResetAllFilters")}
                  </span>
                </div>

                <div className="scrollable-container">
                  <div className="select-no-outline mt-4 d-flex justify-content-center flex-column px-4">
                    <label className="mb-2 ps-1 paragraph fw-b">
                      {t("Property")}
                    </label>
                    <Select
                      className="w-full ps-1 no-radius property-inputs"
                      options={propertyOptions}
                      placeholder={t("SelectProperty")}
                      onChange={(e) => setStagedProperty(e ? e.value : null)}
                      value={
                        stagedProperty && propertyOptions
                          ? propertyOptions.find((x) => x.value === stagedProperty)
                          : null
                      }
                      isClearable
                    />
                  </div>
                  <div className="select-no-outline mt-4 d-flex justify-content-center flex-column px-4">
                    <label className="mb-2 ps-1 paragraph fw-b">
                      {t("Destination")}
                    </label>
                    <Select
                      className="w-full ps-1 no-radius property-inputs"
                      options={areaOptions}
                      placeholder={t("SelectDestination")}
                      onChange={(e) => setStagedDestination(e ? e.value : null)}
                      value={
                        stagedDestination && areaOptions
                          ? areaOptions.find((x) => x.value === stagedDestination)
                          : null
                      }
                      isClearable
                    />
                  </div>
                  <div className="d-flex flex-column">
                    <div className="d-flex justify-content-between mt-5 px-4">
                      <div className="paragraph fw-b">
                        <UnitFieldSymbols
                          key={`staged-bedrooms-${stagedBedrooms}`}
                          label={t("Bedrooms")}
                          value={stagedBedrooms}
                          onChange={handleStagedBedroomChange}
                          className={"px-3 me-1"}
                          labelClassName={"mb-2 ps-1"}
                        />
                      </div>
                      <div className="paragraph fw-b">
                        <UnitFieldSymbols
                          key={`staged-bathrooms-${stagedBathrooms}`}
                          label={t("Bathrooms")}
                          value={stagedBathrooms}
                          onChange={handleStagedBathroomChange}
                          className={"px-3 ms-1"}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="select-no-outline mt-4 d-flex justify-content-center flex-column px-4 mb-4 mt-5">
                    <label className="mb-2 ps-1 paragraph fw-b">
                      {t("NumberOfGuest")}
                    </label>
                    <Select
                      className="w-full ps-1 no-radius property-inputs"
                      options={guestOptions}
                      value={
                        guestOptions && stagedGuests
                          ? guestOptions.find((x) => x.value === stagedGuests)
                          : null
                      }
                      onChange={(e) => setStagedGuests(e ? e.value : null)}
                      isSearchable={false}
                      placeholder={t("SelectNumberOfGuests")}
                      isClearable
                    />
                  </div>

                  <div className="select-no-outlined-flex justify-content-center flex-column px-4 mb-4 mt-5">
                    <div className="d-flex align-items-center px-1 justify-content-between">
                      <label className="mb-2 paragraph fw-b">
                        {t("Characteristics")}
                      </label>
                      <div className="cursor-pointer">
                        <FontAwesomeIcon
                          icon={isVillaOpen ? faMinus : faPlus}
                          className="icon-toggle"
                          onClick={toggleVillaList}
                        />
                      </div>
                    </div>
                    {isVillaOpen && (
                      <div className="d-flex flex-column">
                        {Object.keys(stagedCheckboxes).filter(key => ['privatePool', 'heatedPool', 'airCon', 'hotTub', 'jacuzzi', 'weddingVenue', 'wifi'].includes(key)).map(key => (
                          <div className="form-check mb-2" key={`desktop-${key}`}>
                            <Checkbox
                              inputId={`desktop-${key}`}
                              onChange={() => {
                                setStagedCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
                              }}
                              className={"checkbox-custom me-1"}
                              checked={stagedCheckboxes[key]}
                            />
                            <label className="form-check-label" htmlFor={`desktop-${key}`}>
                              {checkboxLabels[key] || key}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="select-no-outlined-flex justify-content-center flex-column px-4 mb-4 mt-5">
                    <div className="d-flex justify-content-between align-items-center px-1">
                      <label className="mb-2 paragraph fw-b">
                        {t("Environment")}
                      </label>
                      <FontAwesomeIcon
                        icon={isEnvironmentOpen ? faMinus : faPlus}
                        className="icon-toggle cursor-pointer"
                        onClick={toggleEnvironmentList}
                      />
                    </div>
                    {isEnvironmentOpen && (
                      <div className="d-flex flex-column">
                         {Object.keys(stagedCheckboxes).filter(key => ['seaView', 'walkToBeach', 'walkToVillage'].includes(key)).map(key => (
                          <div className="form-check mb-2" key={`desktop-env-${key}`}>
                            <Checkbox
                              inputId={`desktop-env-${key}`}
                              onChange={() => {
                                setStagedCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
                              }}
                              className={"checkbox-custom me-1"}
                              checked={stagedCheckboxes[key]}
                            />
                            <label className="form-check-label" htmlFor={`desktop-env-${key}`}>
                               {checkboxLabels[key] || key}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="justify-content-end d-flex m-3">
                  <button className="applyFilter" onClick={handleApplyFilters}>
                    {t("ApplyFilters")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="container px-4 px-md-5">
              <Accordion defaultActiveKey={null} className="accordion-detail">
                <Accordion.Item className="mb-5">
                  <Accordion.Header>{t("Filter")}</Accordion.Header>
                  <Accordion.Body className="ps-0 pe-0">
                    <div>
                       <div className="d-flex flex-column mt-4">
                        <p>{t("Date")}:</p>
                        <div className="d-flex">
                          <div>
                            <Calendar
                              value={checkInDate}
                              onChange={(e) => {
                                const newCheckIn = e.value ? parseDate(e.value) : null;
                                setCheckInDate(newCheckIn);
                                if (newCheckIn && checkOutDate && newCheckIn >= checkOutDate) {
                                    setCheckOutDate(null);
                                }
                              }}
                              inputClassName="w-full custom-select py-2"
                              className="w-full custom-select col-lg-5 col-md-6 col-6"
                              placeholder={t("AddDate")}
                              dateFormat="dd/mm/yy"
                              minDate={minGlobalDate}
                              readOnlyInput={isMobileTablet}
                              viewDate={checkInDate ? checkInDate : today}
                            />
                            <Calendar
                              value={checkOutDate}
                              onChange={(e) => setCheckOutDate(e.value ? parseDate(e.value) : null)}
                              inputClassName="w-full custom-select py-2"
                              className="w-full custom-select col-lg-5 col-md-6 col-6"
                              placeholder={t("AddDate")}
                              dateFormat="dd/mm/yy"
                              minDate={checkInDate ? addDays(checkInDate, 1) : addDays(minGlobalDate,1)}
                              readOnlyInput={isMobileTablet}
                              disabled={!checkInDate}
                              viewDate={checkOutDate ? checkOutDate : checkInDate}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column mt-4">
                        <p>{t("PropertyType")}:</p>
                        <div className="d-flex justify-content-start">
                          <button
                            className={
                              (isVilla
                                ? "background-black text-white "
                                : "background-white text-black border-custom ") +
                              "btn-filter-property"
                            }
                            onClick={() => setIsVilla(!isVilla)}
                          >
                            {t("Villa")}
                          </button>
                          <button
                            className={
                              (isApartment
                                ? "background-black text-white "
                                : "background-white text-black border-custom ") +
                              "btn-filter-property"
                            }
                            onClick={() => setIsApartment(!isApartment)}
                          >
                            {t("Apartment")}
                          </button>
                        </div>
                      </div>
                      <div className="select-no-outline mt-4 d-flex justify-content-center flex-column">
                        <label className="mb-2 ps-0 paragraph">
                          {t("Property")}
                        </label>
                         <Select
                            className="w-full ps-0 no-radius property-inputs"
                            options={propertyOptions}
                            placeholder={t("SelectProperty")}
                            onChange={(e) => setStagedProperty(e ? e.value : null)}
                            value={stagedProperty && propertyOptions ? propertyOptions.find((x) => x.value === stagedProperty) : null}
                            isClearable
                        />
                      </div>
                      <div className="select-no-outline mt-4 d-flex justify-content-center flex-column">
                        <label className="mb-2 ps-0 paragraph">
                          {t("Destination")}
                        </label>
                        <Select
                          className="w-full ps-0 no-radius property-inputs"
                          options={areaOptions}
                          placeholder={t("SelectDestination")}
                          onChange={(e) => setStagedDestination(e ? e.value : null)}
                          value={
                            stagedDestination && areaOptions
                              ? areaOptions.find((x) => x.value === stagedDestination)
                              : null
                          }
                          isClearable
                        />
                      </div>

                      <div className="d-flex flex-column">
                        <div className="d-flex justify-content-between mt-4">
                          <div className="paragraph">
                            <UnitFieldSymbols
                              key={`staged-bedrooms-mobile-${stagedBedrooms}`}
                              label={t("Bedrooms")}
                              value={stagedBedrooms}
                              onChange={handleStagedBedroomChange}
                              className={"px-3 me-1"}
                              labelClassName={"mb-2 ps-0"}
                            />
                          </div>
                          <div className="paragraph">
                            <UnitFieldSymbols
                              key={`staged-bathrooms-mobile-${stagedBathrooms}`}
                              label={t("Bathrooms")}
                              value={stagedBathrooms}
                              onChange={handleStagedBathroomChange}
                              className={"px-3 ms-1"}
                              labelClassName={"ps-1"}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="select-no-outline d-flex justify-content-center flex-column mt-3">
                        <label className="mb-2 ps-0 paragraph">
                          {t("NumberOfGuest")}
                        </label>
                        <Select
                          className="w-full ps-0 no-radius property-inputs"
                          options={guestOptions}
                          value={
                            guestOptions && stagedGuests
                              ? guestOptions.find((x) => x.value === stagedGuests)
                              : null
                          }
                          onChange={(e) => setStagedGuests(e ? e.value : null)}
                          isSearchable={false}
                          placeholder={t("SelectNumberOfGuests")}
                          isClearable
                        />
                      </div>

                      <div className="select-no-outlined-flex justify-content-center flex-column mt-3">
                        <div className="d-flex align-items-center px-1 justify-content-between">
                          <label className="mb-2 paragraph">
                            {t("Characteristics")}
                          </label>
                          <div className="cursor-pointer">
                            <FontAwesomeIcon
                              icon={isVillaOpen ? faMinus : faPlus}
                              className="icon-toggle"
                              onClick={toggleVillaList}
                            />
                          </div>
                        </div>
                        {isVillaOpen && (
                          <div className="d-flex flex-column">
                            {Object.keys(stagedCheckboxes).filter(key => ['privatePool', 'heatedPool', 'airCon', 'hotTub', 'jacuzzi', 'weddingVenue', 'wifi'].includes(key)).map(key => (
                              <div className="form-check mb-2" key={`mobile-${key}`}>
                                <Checkbox
                                  inputId={`mobile-${key}`}
                                  onChange={() => {
                                    setStagedCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
                                  }}
                                  className={"checkbox-custom me-1"}
                                  checked={stagedCheckboxes[key]}
                                />
                                <label className="form-check-label" htmlFor={`mobile-${key}`}>
                                  {checkboxLabels[key] || key}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="select-no-outlined-flex justify-content-center flex-column mt-3">
                        <div className="d-flex justify-content-between align-items-center px-1">
                          <label className="mb-2 paragraph">
                            {t("Environment")}
                          </label>
                          <FontAwesomeIcon
                            icon={isEnvironmentOpen ? faMinus : faPlus}
                            className="icon-toggle cursor-pointer"
                            onClick={toggleEnvironmentList}
                          />
                        </div>
                        {isEnvironmentOpen && (
                          <div className="d-flex flex-column">
                            {Object.keys(stagedCheckboxes).filter(key => ['seaView', 'walkToBeach', 'walkToVillage'].includes(key)).map(key => (
                              <div className="form-check mb-2" key={`mobile-env-${key}`}>
                                <Checkbox
                                  inputId={`mobile-env-${key}`}
                                  onChange={() => {
                                    setStagedCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
                                  }}
                                  className={"checkbox-custom me-1"}
                                  checked={stagedCheckboxes[key]}
                                />
                                <label className="form-check-label" htmlFor={`mobile-env-${key}`}>
                                  {checkboxLabels[key] || key}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="justify-content-between align-items-center d-flex mt-4">
                      <button
                        className="resetFilter"
                        onClick={handleGlobalResetFilters} 
                      >
                        {t("ResetAllFilters")}
                      </button>

                      <button className="applyFilter" onClick={handleApplyFilters}>
                        {t("ApplyFilters")}
                      </button>
                    </div>
                    <div className="border-bottom border-black mt-4"></div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          )}
          <div className="col-lg-9 mt-md-0 mt-lg-5 px-4 px-md-5 col-md-12 col-12">
            <div className="row d-none d-lg-flex"> 
              <div className="col-lg-4 col-md-12 col-12">
                <p>{t("PropertyType")}:</p>
                <div className="row">
                  <div className="col-lg-4 col-md-2 col-5">
                    <button
                      className={
                        (isVilla
                          ? "background-black text-white "
                          : "background-white text-black border-custom ") +
                        "btn-filter-property"
                      }
                       onClick={() => setIsVilla(!isVilla)}
                    >
                      {t("Villa")}
                    </button>
                  </div>
                  <div className="col-lg-4 col-md-2 col-5">
                    <button
                      className={
                        (isApartment
                          ? "background-black text-white "
                          : "background-white text-black border-custom ") +
                        "btn-filter-property"
                      }
                      onClick={() => setIsApartment(!isApartment)}
                    >
                      {t("Apartment")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 col-12 mt-lg-0 mt-md-3 mt-sm-15">
                <p>{t("Date")}:</p>
                <div className="d-flex">
                  <div>
                    <Calendar
                      value={checkInDate}
                      onChange={(e) => {
                        const newCheckIn = e.value ? parseDate(e.value) : null;
                        setCheckInDate(newCheckIn);
                        if (newCheckIn && checkOutDate && newCheckIn >= checkOutDate) {
                            setCheckOutDate(null);
                        }
                      }}
                      inputClassName="w-full custom-select py-2"
                      className="w-full custom-select col-lg-5 col-md-6 col-6"
                      placeholder={t("AddDate")}
                      dateFormat="dd/mm/yy"
                      minDate={minGlobalDate}
                      readOnlyInput={isMobileTablet}
                      viewDate={checkInDate ? checkInDate : today}
                    />
                    <Calendar
                      value={checkOutDate}
                      onChange={(e) => setCheckOutDate(e.value ? parseDate(e.value) : null)}
                      inputClassName="w-full custom-select py-2"
                      className="w-full custom-select col-lg-5 col-md-6 col-6"
                      placeholder={t("AddDate")}
                      dateFormat="dd/mm/yy"
                      minDate={checkInDate ? addDays(checkInDate, 1) : null}
                      readOnlyInput={isMobileTablet}
                      disabled={!checkInDate}
                      viewDate={checkOutDate ? checkOutDate : checkInDate}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 col-12 mt-lg-0 mt-md-3 mt-sm-15">
                <p>{t("SortBy")}:</p>
                <div className="d-flex w-100">
                  <Select
                    className="w-100 ps-1 no-radius property-inputs"
                    options={sortingOptions}
                    value={sortBy}
                    onChange={(option) => setSortBy(option)}
                    placeholder={t("Select")}
                    isSearchable={false}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 mt-5">
              <span>
                {properties.length} {properties.length === 1 ? t("PropertyAvailable") : t("PropertiesAvailable")}
              </span>
            </div>
            <div className="row mt-5">
              {isLoadingAvailability && (
                  <div className="col-12">
                    <LoadingBar text={t("LoadingAvailability")} />
                  </div>
                )}              
              {!isLoadingAvailability && currentItems && currentItems.length > 0 &&
                currentItems?.map((item, index) => (
                  <div className="col-lg-4 col-md-6 col-12" key={item.id || index}>
                    <CardStandard card={item} />
                  </div>
                ))}
            </div>
            <div className="col-12 d-flex justify-content-center mt-5 mb-5">
              <button
                id="pagination-button-prev"
                onClick={prevPage}
                disabled={currentPage === 1}
                className="bg-transparent border-none"
              >
                <ChevronLeft />
              </button>
              {currentPage} {t("of")}{" "}
              {properties.length === 0
                ? 1
                : Math.ceil(properties.length / itemsPerPage)}
              <button
                id="pagination-button-next"
                onClick={nextPage}
                disabled={
                  currentPage ===
                  (properties.length === 0
                    ? 1
                    : Math.ceil(properties.length / itemsPerPage)) || properties.length === 0
                }
                className="bg-transparent border-none"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyCollection;